/**
 * Favorites Management
 *
 * For anonymous users: stores favorites in localStorage
 * For logged-in users: uses the API to store in database
 */

import type { DataClient } from '@slowmaui/api-client';

const STORAGE_KEY = 'slowmaui_favorites';

type FavoritesChangeCallback = (favorites: Set<string>) => void;

let dataClient: DataClient | null = null;
let isLoggedIn = false;
let favorites: Set<string> = new Set();
let listeners: FavoritesChangeCallback[] = [];

/**
 * Parse item key to extract itemId and itemType
 * Properties: TMK normalized string (e.g., "2-4-2-019-024-0001")
 * Neighborhoods: "nh:slug" format
 */
function parseItemKey(key: string): { itemId: string; itemType: 'property' | 'neighborhood' } {
  if (key.startsWith('nh:')) {
    return { itemId: key.substring(3), itemType: 'neighborhood' };
  }
  return { itemId: key, itemType: 'property' };
}

/**
 * Build item key from itemId and itemType
 */
function buildItemKey(itemId: string, itemType: 'property' | 'neighborhood'): string {
  return itemType === 'neighborhood' ? `nh:${itemId}` : itemId;
}

/**
 * Load favorites from localStorage (for anonymous users)
 */
function loadFromLocalStorage(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        favorites = new Set(parsed);
      }
    }
  } catch (error) {
    console.error('Failed to load favorites from localStorage:', error);
    favorites = new Set();
  }
}

/**
 * Save favorites to localStorage (for anonymous users)
 */
function saveToLocalStorage(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...favorites]));
  } catch (error) {
    console.error('Failed to save favorites to localStorage:', error);
  }
}

/**
 * Get localStorage favorites in API format (for syncing on first login)
 */
export function getLocalStorageFavorites(): Array<{ itemId: string; itemType: 'property' | 'neighborhood' }> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.map(key => parseItemKey(key));
      }
    }
  } catch (error) {
    console.error('Failed to get localStorage favorites:', error);
  }
  return [];
}

/**
 * Clear localStorage favorites (after successful sync to DB)
 */
export function clearLocalStorageFavorites(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear localStorage favorites:', error);
  }
}

/**
 * Notify all listeners of favorites change
 */
function notifyListeners(): void {
  listeners.forEach(callback => callback(new Set(favorites)));
}

/**
 * Initialize favorites for anonymous user (localStorage)
 */
export function initFavorites(): void {
  isLoggedIn = false;
  dataClient = null;
  loadFromLocalStorage();
  notifyListeners();
}

/**
 * Initialize favorites for logged-in user (API)
 * @param client - DataClient instance with auth token set
 * @param serverFavorites - Favorites loaded from server
 */
export function initFavoritesForUser(
  client: DataClient,
  serverFavorites: Array<{ itemId: string; itemType: 'property' | 'neighborhood' }>
): void {
  dataClient = client;
  isLoggedIn = true;
  favorites = new Set(serverFavorites.map(f => buildItemKey(f.itemId, f.itemType)));
  notifyListeners();
}

/**
 * Reset to anonymous mode (on logout)
 */
export function resetFavoritesToAnonymous(): void {
  isLoggedIn = false;
  dataClient = null;
  loadFromLocalStorage();
  notifyListeners();
}

/**
 * Get all favorites
 */
export function getFavorites(): Set<string> {
  return new Set(favorites);
}

/**
 * Check if item is a favorite
 */
export function isFavorite(id: string): boolean {
  return favorites.has(id);
}

/**
 * Toggle favorite status
 */
export async function toggleFavorite(id: string): Promise<boolean> {
  const wasFavorite = favorites.has(id);
  const { itemId, itemType } = parseItemKey(id);

  if (wasFavorite) {
    favorites.delete(id);
  } else {
    favorites.add(id);
  }

  // Optimistic update - notify immediately
  notifyListeners();

  if (isLoggedIn && dataClient) {
    try {
      if (wasFavorite) {
        await dataClient.removeFavorite(itemId);
      } else {
        await dataClient.addFavorite(itemId, itemType);
      }
    } catch (error) {
      // Revert on error
      console.error('Failed to update favorite:', error);
      if (wasFavorite) {
        favorites.add(id);
      } else {
        favorites.delete(id);
      }
      notifyListeners();
      throw error;
    }
  } else {
    // Save to localStorage for anonymous users
    saveToLocalStorage();
  }

  return favorites.has(id);
}

/**
 * Add to favorites
 */
export async function addFavorite(id: string): Promise<void> {
  if (favorites.has(id)) return;

  const { itemId, itemType } = parseItemKey(id);
  favorites.add(id);
  notifyListeners();

  if (isLoggedIn && dataClient) {
    try {
      await dataClient.addFavorite(itemId, itemType);
    } catch (error) {
      console.error('Failed to add favorite:', error);
      favorites.delete(id);
      notifyListeners();
      throw error;
    }
  } else {
    saveToLocalStorage();
  }
}

/**
 * Remove from favorites
 */
export async function removeFavorite(id: string): Promise<void> {
  if (!favorites.has(id)) return;

  const { itemId } = parseItemKey(id);
  favorites.delete(id);
  notifyListeners();

  if (isLoggedIn && dataClient) {
    try {
      await dataClient.removeFavorite(itemId);
    } catch (error) {
      console.error('Failed to remove favorite:', error);
      favorites.add(id);
      notifyListeners();
      throw error;
    }
  } else {
    saveToLocalStorage();
  }
}

/**
 * Subscribe to favorites changes
 */
export function onFavoritesChange(callback: FavoritesChangeCallback): () => void {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter(l => l !== callback);
  };
}

/**
 * Get favorites count
 */
export function getFavoritesCount(): number {
  return favorites.size;
}

/**
 * Check if currently using API (logged in) or localStorage
 */
export function isUsingApi(): boolean {
  return isLoggedIn;
}
